import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/SimpleAuthContext';
import ProfileAvatar from '../components/profile/ProfileAvatar';
import API_CONFIG from '../config/apiConfig';
import { AnnouncementsCard } from '../components/announcements';
import WithdrawApplicationModal from '../components/course/WithdrawApplicationModal';
import DocumentUploadModal from '../components/registrations/DocumentUploadModal';
import {
  CAREER_PASSPORT_DEFINITIONS,
  getCoursePassportProgressions,
  getPassportProgressState
} from '../config/careerPassports';
import DashboardSummary from '../components/dashboard/DashboardSummary';
import DashboardLearning from '../components/dashboard/DashboardLearning';
import DashboardRegistrations from '../components/dashboard/DashboardRegistrations';
import DashboardPassports from '../components/dashboard/DashboardPassports';
import DashboardSavedCourses from '../components/dashboard/DashboardSavedCourses';
import './DashboardLayout.css';

const getEnrollmentProgress = (enrollment) => {
  if (enrollment?.progress_percentage == null) return null;
  const progress = Number(enrollment.progress_percentage);
  return Number.isFinite(progress) ? Math.max(0, Math.min(100, progress)) : null;
};

const calculateCoursePassportProgressions = (course, enrollment, registration) => {
  const progressions = getCoursePassportProgressions(course);
  const progress = getEnrollmentProgress(enrollment);
  const registrationStatus = registration?.status;
  const completed = progress === 100 || ['completed', 'certificate_awarded'].includes(enrollment?.status) ||
    registrationStatus === 'completed';

  return progressions.map((progression) => ({
    ...progression,
    progress,
    completed,
    courseName: course?.programme_name || 'Linked course',
    targetLabel: progression.targetLabel,
    sourceLabel: enrollment
      ? 'LMS course activity'
      : 'Course registration'
  }));
};

const Dashboard = () => {
  const { user, isAuthenticated, makeAuthenticatedRequest } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_applications: 0,
    active_courses: 0,
    completed_courses: 0,
    wishlist_items: 0,
    lms_learning: 0,
    lms_completed_courses: 0
  });
  const [registrations, setRegistrations] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [lmsEnrollments, setLmsEnrollments] = useState([]);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadRegistrationId, setUploadRegistrationId] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 Dashboard: Starting data fetch for user:', user?.email);

      // Fetch dashboard stats
      const statsResponse = await makeAuthenticatedRequest(API_CONFIG.registrations.dashboardStats());
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
        console.log('✅ Loaded dashboard stats:', statsData);
      } else {
        console.error('Failed to fetch dashboard stats:', statsResponse.status);
      }

      // Fetch user registrations
      const registrationsResponse = await makeAuthenticatedRequest(API_CONFIG.registrations.myRegistrations());
      if (registrationsResponse.ok) {
        const registrationsData = await registrationsResponse.json();
        setRegistrations(registrationsData);
        console.log('✅ Loaded', registrationsData.length, 'registrations');
      } else {
        console.error('Failed to fetch registrations:', registrationsResponse.status);
      }

      // Fetch wishlist
      const wishlistResponse = await makeAuthenticatedRequest('http://localhost:8000/api/registrations/wishlist/');
      if (wishlistResponse.ok) {
        const wishlistData = await wishlistResponse.json();
        console.log('Dashboard wishlist data:', wishlistData);

        // Handle paginated response
        const wishlistItems = wishlistData.results || wishlistData;
        setWishlist(wishlistItems);
        console.log('✅ Loaded', wishlistItems.length, 'wishlist items');

        // Update stats with actual wishlist count
        setStats(prevStats => ({
          ...prevStats,
          wishlist_items: wishlistItems.length
        }));
      } else {
        console.error('Failed to fetch wishlist:', wishlistResponse.status);
      }

      // Fetch LMS enrollments
      const lmsResponse = await makeAuthenticatedRequest(API_CONFIG.lms.enrollments());
      if (lmsResponse.ok) {
        const lmsData = await lmsResponse.json();
        const lmsItems = Array.isArray(lmsData) ? lmsData : (lmsData?.results || []);
        setLmsEnrollments(lmsItems);
        console.log('âœ… Loaded', lmsItems.length, 'LMS enrollments');
      } else {
        console.error('Failed to fetch LMS enrollments:', lmsResponse.status);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, user?.email]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
  }, [isAuthenticated, fetchDashboardData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLmsEnrollmentClick = (enrollment) => {
    if (!enrollment?.public_id) return;

    navigate(`/learning/${enrollment.public_id}`);
  };

  const getLmsProgress = (enrollment) => {
    const progress = Number(enrollment?.progress_percentage ?? 0);
    if (Number.isNaN(progress)) return 0;
    return Math.max(0, Math.min(100, progress));
  };

  const getLmsActionLabel = (enrollment) => {
    if (!enrollment) return 'View progress';
    if (enrollment.status === 'pending_approval') return 'View application';
    if (enrollment.status === 'rejected') return 'View outcome';
    if (enrollment.can_launch) {
      return 'Continue learning';
    }
    if (['completed', 'certificate_awarded'].includes(enrollment.status)) {
      return 'Review learning';
    }
    return 'Track handoff';
  };

  const lmsSummary = useMemo(() => {
    const activeEnrollments = lmsEnrollments.filter((enrollment) =>
      ['provisioning', 'ready_to_launch', 'active', 'paused', 'sync_failed'].includes(enrollment.status)
    );

    const averageProgress = activeEnrollments.length
      ? Math.round(
          activeEnrollments.reduce((sum, enrollment) => sum + getLmsProgress(enrollment), 0) /
            activeEnrollments.length
        )
      : 0;

    const latestEnrollment = [...lmsEnrollments].sort((a, b) => {
      const aDate = new Date(a.last_synced_at || a.updated_at || a.accepted_at || 0).getTime();
      const bDate = new Date(b.last_synced_at || b.updated_at || b.accepted_at || 0).getTime();
      return bDate - aDate;
    })[0] || null;

    return {
      activeCount: activeEnrollments.length,
      averageProgress,
      latestEnrollment,
      nextAction: latestEnrollment ? getLmsActionLabel(latestEnrollment) : 'Explore recommendations'
    };
  }, [lmsEnrollments]);

  const passportProgress = useMemo(() => {
    const progressByPassport = new Map();
    const lmsByCourse = new Map(
      lmsEnrollments
        .filter((enrollment) => enrollment?.course?.id)
        .map((enrollment) => [String(enrollment.course.id), enrollment])
    );

    registrations.forEach((registration) => {
      const course = registration?.course;
      if (!course) return;

      calculateCoursePassportProgressions(
        course,
        lmsByCourse.get(String(course.id)),
        registration
      ).forEach((progression) => {
        const current = progressByPassport.get(progression.id);
        const currentProgress = current?.progress ?? -1;
        const nextProgress = progression.progress ?? -1;
        if (!current || nextProgress > currentProgress || progression.completed) {
          progressByPassport.set(progression.id, progression);
        }
      });
    });

    lmsEnrollments.forEach((enrollment) => {
      const course = enrollment?.course;
      if (!course) return;

      calculateCoursePassportProgressions(course, enrollment, null).forEach((progression) => {
        const current = progressByPassport.get(progression.id);
        const currentProgress = current?.progress ?? -1;
        const nextProgress = progression.progress ?? -1;
        if (!current || nextProgress > currentProgress || progression.completed) {
          progressByPassport.set(progression.id, progression);
        }
      });
    });

    return CAREER_PASSPORT_DEFINITIONS.map((passport) => {
      const current = progressByPassport.get(passport.id);
      const achievement = getPassportProgressState(passport.id, lmsEnrollments);
      const hasEvidence = Boolean(current);
      const progress = current?.progress ?? null;
      const earned = achievement.earnedLabel;

      return {
        ...passport,
        ...current,
        standards: achievement.standards,
        standardComplete: achievement.standardComplete,
        allStandardsComplete: achievement.allStandardsComplete,
        hasEvidence,
        progress,
        progressionLabel: earned || (current?.progressionLabel ? `Course target: ${current.progressionLabel}` : ''),
        statusLabel: earned
          ? achievement.standardComplete
            ? `${earned} complete`
            : earned
          : hasEvidence
          ? current.completed
            ? 'Course completed'
            : current.progress !== null
              ? 'In progress'
              : 'Pathway started'
          : 'Not started',
        levelLabel: hasEvidence
          ? current.targetLabel
          : 'Start with a course or career assessment',
        nextStep: achievement.allStandardsComplete
          ? 'All three Passport Standards are complete.'
          : achievement.standardComplete
            ? 'This Standard is complete. Continue with the next Standard when ready.'
          : earned
            ? 'Continue through the next belt in this Standard.'
          : hasEvidence
          ? current.completed
            ? 'Course completed; a Passport Belt is awarded after the linked LMS assessment is passed.'
            : 'Continue your linked course'
          : 'Explore courses linked to this Passport'
      };
    });
  }, [lmsEnrollments, registrations]);

  const removeFromWishlist = async (wishlistId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `http://localhost:8000/api/registrations/wishlist/${wishlistId}/`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setWishlist(prev => prev.filter(item => item.id !== wishlistId));
        setStats(prev => ({ ...prev, wishlist_items: prev.wishlist_items - 1 }));
        console.log('✅ Removed item from wishlist');
      } else {
        console.error('Failed to remove from wishlist:', response.status);
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  // Check if withdrawal is allowed based on status
  const canWithdrawApplication = (status) => {
    const withdrawableStatuses = ['applied', 'under_review', 'accepted', 'to_register'];
    return withdrawableStatuses.includes(status);
  };

  // Handle withdraw button click
  const handleWithdrawClick = (e, registration) => {
    e.stopPropagation(); // Prevent course navigation
    setSelectedRegistration(registration);
    setWithdrawError('');
    setShowWithdrawModal(true);
  };

  const handleUploadClick = (e, registrationId) => {
    e.stopPropagation();
    setUploadRegistrationId(registrationId);
    setShowUploadModal(true);
  };

  // Handle withdrawal confirmation
  const handleWithdrawConfirm = async () => {
    if (!selectedRegistration) return;

    try {
      setWithdrawLoading(true);
      const response = await makeAuthenticatedRequest(
        API_CONFIG.registrations.withdraw(selectedRegistration.id),
        { method: 'POST' }
      );

      if (response.ok) {
        // Update the registration status locally
        setRegistrations(prev =>
          prev.map(reg =>
            reg.id === selectedRegistration.id
              ? { ...reg, status: 'withdrawn', status_display: 'Withdrawn' }
              : reg
          )
        );
        setShowWithdrawModal(false);
        setSelectedRegistration(null);
        setWithdrawError('');
        console.log('✅ Application withdrawn successfully');
      } else {
        let responseData = null;
        try {
          responseData = await response.json();
        } catch {
          // Use the status-based message below when the response is not JSON.
        }
        const message = responseData?.error || responseData?.detail || responseData?.message;
        setWithdrawError(message || `We couldn't withdraw this application (error ${response.status}). Please try again.`);
        console.error('Failed to withdraw application:', response.status, message || 'No server message');
      }
    } catch (error) {
      console.error('Error withdrawing application:', error);
      setWithdrawError('We couldn’t reach the server. Check your connection and try again.');
    } finally {
      setWithdrawLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="learner-loading" role="status">
          <span className="learner-spinner" aria-hidden="true" />
          <p>Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="learner-dashboard">
        <header className="learner-header">
          <div className="learner-welcome">
            <ProfileAvatar
              src={user?.profile_picture_url}
              alt={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'Your profile'}
              size="large"
              showVerifiedBadge={true}
              isVerified={user?.is_verified}
            />
            <div>
              <h1>Welcome back{user?.first_name ? `, ${user.first_name}` : ''}!</h1>
              <p>Track your learning and take your next step.</p>
            </div>
          </div>
          <div className="learner-header-actions">
            <Link to="/profile" className="learner-button learner-button--outline">Edit profile</Link>
            <Link to="/courses" className="learner-button learner-button--gold">Browse courses</Link>
          </div>
        </header>

        <DashboardSummary stats={stats} />

        <div className="learner-workspace">
          <div className="learner-main">
            <DashboardLearning
              enrollments={lmsEnrollments}
              summary={lmsSummary}
              getProgress={getLmsProgress}
              getActionLabel={getLmsActionLabel}
              formatDateTime={formatDateTime}
              onOpen={handleLmsEnrollmentClick}
              onViewStatus={enrollment => navigate(`/learning/${enrollment.public_id}`)}
              onExplore={() => navigate('/career/recommendations')}
            />
            <DashboardRegistrations
              registrations={registrations}
              formatDate={formatDate}
              canWithdraw={canWithdrawApplication}
              onUpload={handleUploadClick}
              onWithdraw={handleWithdrawClick}
            />
          </div>
          <aside className="learner-rail" aria-label="Your pathways and updates">
            <DashboardPassports passports={passportProgress} />
            <DashboardSavedCourses wishlist={wishlist} formatDate={formatDate} onRemove={removeFromWishlist} />
            <section className="learner-panel learner-announcements" aria-labelledby="learner-announcements-title">
              <header className="learner-panel-heading">
                <h2 id="learner-announcements-title">Announcements</h2>
              </header>
              <div className="learner-announcements-body">
                <AnnouncementsCard maxItems={3} showWishlistDeadlines={true} />
              </div>
            </section>
          </aside>
        </div>
      </div>

      <WithdrawApplicationModal
        isOpen={showWithdrawModal}
        onClose={() => {
          if (withdrawLoading) return;
          setShowWithdrawModal(false);
          setSelectedRegistration(null);
          setWithdrawError('');
        }}
        onConfirm={handleWithdrawConfirm}
        courseName={selectedRegistration?.course?.programme_name}
        registrationId={selectedRegistration?.id}
        isLoading={withdrawLoading}
        error={withdrawError}
      />
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        registrationId={uploadRegistrationId}
        onUploadSuccess={fetchDashboardData}
      />
    </div>
  );
};

export default Dashboard;
